/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import classNames from 'classnames';

import _t from 'i18n!nls/payments';

type OptionType = {
  className?: string;
  label?: string;
  title?: string;
  [key: string]: string | number | boolean | undefined;
};

type Props = {
  children?: React.ReactNode;
  className?: string;
  instancePrefix?: string;
  isDisabled?: boolean;
  isFocused?: boolean;
  isSelected?: boolean;
  onFocus?: (option: OptionType, event: React.SyntheticEvent<HTMLDivElement>) => void;
  onSelect?: (option: OptionType, event: React.SyntheticEvent<HTMLDivElement>) => void;
  onUnfocus?: (option: OptionType, event: React.SyntheticEvent<HTMLDivElement>) => void;
  option: OptionType;
  optionIndex?: number;
};

/**
 * This component is based of react-select 1.3's default Option component, with some a11y improvements
 *
 * Note: it needs to be a class component to work with react-select 1.3, because it attaches refs directly.
 * Function components (even with forwardRef) are not supported in this version.
 */
class LocationFormSelectOption extends React.Component<Props> {
  dragging: boolean = false;

  handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    this.props.onSelect?.(this.props.option, event);
  };

  handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
    this.onFocus(event);
  };

  handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    this.onFocus(event);
  };

  handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (this.dragging) return;
    this.handleMouseDown(event as unknown as React.MouseEvent<HTMLDivElement>);
  };

  handleTouchMove = () => {
    this.dragging = true;
  };

  handleTouchStart = () => {
    this.dragging = false;
  };

  onFocus = (event: React.SyntheticEvent<HTMLDivElement>) => {
    if (!this.props.isFocused) {
      this.props.onFocus?.(this.props.option, event);
    }
  };

  render() {
    const { option, instancePrefix, optionIndex, className, children, isSelected } = this.props;
    const combinedClassName = classNames(className, option.className);
    const ariaLabelItems = [option.label, isSelected && _t('selected')];
    const ariaLabel = ariaLabelItems.filter(Boolean).join(', ');

    return (
      <div
        className={combinedClassName}
        role="option"
        aria-label={ariaLabel}
        aria-selected={isSelected}
        tabIndex={-1}
        onMouseDown={this.handleMouseDown}
        onMouseEnter={this.handleMouseEnter}
        onMouseMove={this.handleMouseMove}
        onTouchStart={this.handleTouchStart}
        onTouchMove={this.handleTouchMove}
        onTouchEnd={this.handleTouchEnd}
        id={`${instancePrefix}-option-${optionIndex}`}
        title={option.title}
      >
        {children}
      </div>
    );
  }
}

export default LocationFormSelectOption;
